<template>
  <div class="container mx-auto px-4 py-8">
    <div class="flex items-center mb-8">
      <el-button @click="$router.back()" type="primary" plain color="#006400">
        <el-icon><ArrowLeft /></el-icon> Back
      </el-button>
      <h1 class="text-3xl font-bold ml-4">{{ pageTitle }}</h1>
    </div>

    <el-card class="mb-6">
      <template #header>
        <div class="flex justify-between items-center">
          <span class="font-bold">TCR Information</span>
          <el-input
            v-model="searchQuery"
            placeholder="Search TCRs..."
            style="width: 300px"
            clearable
            @input="handleSearchInput"
          >
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
        </div>
      </template>

      <el-table
        :data="filteredTCRs"
        style="width: 100%"
        height="600"
        :default-sort="{ prop: 'pdb_id', order: 'ascending'}"
      >
        <!-- <el-table-column type="expand">
          <template #default="props">
            <div class="p-4">
              <el-descriptions :column="3" border>
                <el-descriptions-item label="TRAV">
                  {{ props.row.alpha_chain }}
                </el-descriptions-item>
                <el-descriptions-item label="TRBV">
                  {{ props.row.beta_chain }}
                </el-descriptions-item>
              </el-descriptions>
              <el-descriptions :column="3" border>
                <el-descriptions-item label="MHC Class">
                  {{ props.row.mhc_class }}
                </el-descriptions-item>
              </el-descriptions>
            </div>
          </template>
        </el-table-column> -->

        <el-table-column
          prop="tcr_name"
          label="TCR Name"
          sortable
          width="150"
        />

        <el-table-column
          prop="CDR3a"
          label="CDR3α"
          sortable
          width="200"
        />

        <el-table-column
          prop="CDR3b"
          label="CDR3β"
          sortable
          width="200"
        />

        <el-table-column
          prop="mhc_allele"
          label="MHC Allele"
          sortable
          width="150"
        />

        <el-table-column
          prop="epitope_info"
          label="Epitope"
          sortable
          width="200"
        />

        <el-table-column
          prop="function"
          label="Function"
          sortable
          width="150"
        />
        <el-table-column
          fixed="right"
          label="Operations"
          width="120"
        >
          <template #default="scope">
            <el-button
              link
              type="primary"
              @click="viewTCRDetail(scope.row.complex_id)"
              style="color: #006400;"
            >
              View Details
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="flex justify-between items-center mt-4">
        <span class="text-green-800">

        </span>
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next"
          :total="totalTCRs"
          background
          class="green-pagination"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ArrowLeft, Search } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { tcrApi } from '@/api/tcr'

const route = useRoute()
const router = useRouter()
const fullDisease = ref(decodeURIComponent(route.params.disease as string))
const [category, diseaseName] = fullDisease.value.split(':')
const disease = ref(diseaseName)
const diseaseCategory = ref(category)

// 更新页面标题显示
const pageTitle = computed(() => {
  return `${diseaseCategory.value}: ${disease.value}`
})

interface TCRData {
  complex_id: string;
  pdb_id: string;
  tcr_name: string;
  CDR3a: string;
  CDR3b: string;
  alpha_chain: string;
  beta_chain: string;
  mhc_allele: string;
  epitope_info: string;
  mhc_class: string;
  function: string;
}

const searchQuery = ref('')
const currentPage = ref(1)
const pageSize = ref(10)
const totalTCRs = ref(0)
const tcrData = ref<TCRData[]>([])

// 从后端获取TCR数据
const fetchTCRData = async () => {
  try {
    const { results, count } = await tcrApi.searchByDisease(
      diseaseCategory.value,
      disease.value,
      currentPage.value,
      pageSize.value
    )

    const funcMap: Record<number, string> = { 0: 'No', 1: 'Yes' };

    tcrData.value = results.map((tcr: any) => ({
      complex_id: tcr.complex_id,
      pdb_id: tcr.pdb_id,
      tcr_name: tcr.tcr_name,
      CDR3a: tcr.cdr3a,
      CDR3b: tcr.cdr3b,
      alpha_chain: tcr.trav,
      beta_chain: tcr.trbv,
      mhc_allele: [tcr.mhc_a, tcr.mhc_b].filter(Boolean).join('\n'),
      epitope_info: `${tcr.epitope_gene || ''}: ${tcr.epitope || ''}`.trim(),
      mhc_class: tcr.mhc_class,
      function: funcMap[tcr.function as keyof typeof funcMap] ?? 'N/A'
    }))
    totalTCRs.value = count
  } catch (error) {
    console.error('Error fetching TCR data:', error)
  }
}

// const filteredTCRs = computed(() => {
//   if (!searchQuery.value) return tcrData.value
//   const query = searchQuery.value.toLowerCase()
//   return tcrData.value.filter((tcr: any) => {
//     return (
//       tcr.pdb_id.toLowerCase().includes(query) ||
//       tcr.alpha_chain.toLowerCase().includes(query) ||
//       tcr.beta_chain.toLowerCase().includes(query) ||
//       tcr.mhc_allele.toLowerCase().includes(query) ||
//       tcr.epitope.toLowerCase().includes(query) ||
//       tcr.tcr_name.toLowerCase().includes(query)
//     )
//   })
// })

const handleSearchInput = () => {
  debouncedFilterTCRs()
}

function debounce<T extends (...args: any[]) => void>(
  fn: T,
  delay: number 
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn.apply(null, args), delay);
  };
}

const debouncedFilterTCRs = debounce(() => {
  // 不需要直接赋值，因为 filteredTCRs 是计算属性
  // 只需要触发重新计算即可
}, 300)

const filterTCRsBySequence = () => {
  if (!searchQuery.value.trim()) return tcrData.value
  
  const query = searchQuery.value.trim().toLowerCase()

  return tcrData.value.filter((tcr) => {
    const alphaMatch = tcr.alpha_chain?.toLowerCase().includes(query) || false
    const betaMatch = tcr.beta_chain?.toLowerCase().includes(query) || false
    
    const otherMatch = 
      tcr.tcr_name?.toLowerCase().includes(query) ||
      tcr.CDR3a?.toLowerCase().includes(query) ||
      tcr.CDR3b?.toLowerCase().includes(query) ||
      tcr.mhc_allele?.toLowerCase().includes(query) ||
      tcr.epitope_info?.toLowerCase().includes(query) ||
      false
    
    return alphaMatch || betaMatch || otherMatch
  })
}

const filteredTCRs = computed(() => {
  return filterTCRsBySequence()
})

const viewTCRDetail = (complex_id: string) => {
  router.push(`/tcr/${complex_id}`)
}

const handleSizeChange = (val: number) => {
  pageSize.value = val
  fetchTCRData()
}

const handleCurrentChange = (val: number) => {
  currentPage.value = val
  fetchTCRData()
}

onMounted(() => {
  // console.log('Component mounted, fetching data...')
  fetchTCRData()
})

watch(() => route.params.disease, (newDisease) => {
  if (newDisease) {
    // console.log('Disease parameter changed:', newDisease)
    const [newCategory, newDiseaseName] = decodeURIComponent(newDisease as string).split(':')
    diseaseCategory.value = newCategory
    disease.value = newDiseaseName
    fetchTCRData()
  }
})
</script>

<style scoped>
.el-descriptions {
  margin: 20px 0;
}

/* 表格排序箭头样式 */
:deep(.el-table .ascending .sort-caret.ascending) {
  border-bottom-color: #166534 !important;
}

:deep(.el-table .descending .sort-caret.descending) {
  border-top-color: #166534 !important;
}

/* 表格头部颜色 */
:deep(.el-table th.el-table__cell) {
  background-color: #f0fdf4 !important;
  color: #166534 !important;
}

/* 分页器样式 */
:deep(.el-pagination.is-background .el-pager li:not(.is-disabled).is-active) {
  background-color: #166534 !important;
  color: white !important;
}

:deep(.el-pagination.is-background .el-pager li:not(.is-disabled):not(.is-active)) {
  background-color: white !important;
  color: #166534 !important;
}

:deep(.el-pagination.is-background .el-pager li:not(.is-disabled):not(.is-active):hover) {
  color: #15803d !important;
}

:deep(.el-pagination .btn-prev),
:deep(.el-pagination .btn-next) {
  background-color: white !important;
  color: #166534 !important;
}

:deep(.el-pagination .btn-prev:hover),
:deep(.el-pagination .btn-next:hover) {
  color: #15803d !important;
}

:deep(.el-pagination__sizes .el-input__wrapper) {
  background-color: white !important;
  box-shadow: 0 0 0 1px #166534 !important;
}

:deep(.el-pagination__sizes .el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px #166534 !important;
}

:deep(.el-pagination__sizes .el-select__caret) {
  color: #166534 !important;
}

:deep(.el-pagination__sizes .el-select__placeholder) {
  color: #166534 !important;
}

:deep(.el-pagination__sizes .el-select__selection) {
  color: #166534 !important;
}

/* 下拉框展开时的样式 */
:deep(.el-select-dropdown.is-active) {
  border-color: #166534 !important;
}

:deep(.el-select .el-select__wrapper.is-focus) {
  box-shadow: 0 0 0 1px #166534 !important;
}

:deep(.el-select:hover .el-input__wrapper) {
  box-shadow: 0 0 0 1px #166534 !important;
}

/* 修复点击和选中状态的样式 */
:deep(.el-select__wrapper.is-focused){
  box-shadow: 0 0 0 1px #166534 !important;
}

:deep(.el-select-dropdown__item.selected),
:deep(.el-select-dropdown__item[aria-selected="true"]) {
  color: #166534 !important;
  background-color: #dcfce7 !important;
}

:deep(.el-select-dropdown__item.selected),
:deep(.el-select-dropdown__item[aria-selected="true"]) {
  color: #166534 !important;
  font-weight: bold !important;
}

:deep(.el-select-dropdown__item.hover),
:deep(.el-select-dropdown__item:hover) {
  background-color: #f0fdf4 !important;
}

:deep(.el-pagination__sizes .el-input__inner) {
  color: #166534 !important;
}

:deep(.el-pagination__total) {
  color: #166534 !important;
}
</style>
